import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, AlertCircle, Loader2, Filter, PauseCircle, PlayCircle, Link as LinkIcon, RefreshCw, Copy, Trash2, RotateCcw, Cake, PhoneOutgoing, Megaphone, MessageSquareText } from "lucide-react";

export const statusBadge = {
  success: { tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Delivered" },
  failed:  { tone: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle, label: "Failed" },
  skipped: { tone: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle, label: "Skipped" },
  pending: { tone: "bg-sky-50 text-sky-700 border-sky-200", icon: Loader2, label: "Pending" },
};

export function SumTile({ label, value, tone, testid }) {
  const map = {
    slate: "bg-white border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
  };
  return (
    <div className={`rounded-2xl border p-4 ${map[tone]}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{fontFamily:"Outfit"}}>{value}</div>
    </div>
  );
}

export function StatusBadgeMini({ status }) {
  const s = statusBadge[status] || statusBadge.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.tone}`}>
      <Icon className={`h-3 w-3 ${status === "pending" ? "animate-spin" : ""}`} /> {s.label}
    </span>
  );
}

export function CallQueueIndicator({ queue, peak = 0 }) {
  if (!queue) return null;
  const active = queue.active || 0;
  const queued = queue.queued || 0;
  const total = active + queued;
  const busy = total > 0;

  if (!busy) {
    return (
      <div
        className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-500"
        data-testid="call-queue-indicator"
        title={`At most ${queue.max_parallel} calls dial at once; the rest wait in a FIFO queue.`}
      >
        <span className="relative flex h-2.5 w-2.5 opacity-50">
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-slate-400" />
        </span>
        <span data-testid="call-queue-text">Call queue idle</span>
        <span className="text-[11px] text-slate-400 font-normal">(max {queue.max_parallel} at a time)</span>
      </div>
    );
  }

  const denom = Math.max(peak, total);
  const sent = Math.max(0, denom - total);
  const pct = denom > 0 ? Math.round((sent / denom) * 100) : 0;

  return (
    <div
      className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 max-w-md"
      data-testid="call-queue-indicator"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          Live call queue
        </div>
        <span className="text-[11px] text-emerald-700/70 font-medium">max {queue.max_parallel} at a time</span>
      </div>

      <div className="mt-3 flex items-stretch gap-3">
        <div className="flex-1 rounded-xl bg-white/70 border border-emerald-100 px-3 py-2">
          <div className="text-2xl font-bold text-emerald-700 leading-none" data-testid="call-queue-dialing">{active}</div>
          <div className="text-[11px] uppercase tracking-wider text-emerald-600/80 mt-1">Dialing now</div>
        </div>
        <div className="flex-1 rounded-xl bg-white/70 border border-amber-100 px-3 py-2">
          <div className="text-2xl font-bold text-amber-600 leading-none" data-testid="call-queue-waiting">{queued}</div>
          <div className="text-[11px] uppercase tracking-wider text-amber-600/80 mt-1">Waiting</div>
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] text-emerald-700/80 mb-1">
          <span data-testid="call-queue-text">{active} dialing · {queued} queued</span>
          <span>{sent} of {denom} sent</span>
        </div>
        <div className="h-2 w-full rounded-full bg-emerald-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${pct}%` }}
            data-testid="call-queue-progress"
          />
        </div>
      </div>
    </div>
  );
}

export function VoiceCallsFilters({
  isAdmin, meta, classFilter, setClassFilter, sectionFilter, setSectionFilter,
  date, setDate, kindFilter, setKindFilter, status, setStatus, showKind = true,
  purgePreview, onOpenTemplates, onOpenAnnounce, onOpenPurge,
  showFilters = true, showActions = true,
}) {
  return (
    <div className="flex items-end gap-3 flex-wrap">
      {showFilters && isAdmin && (
        <>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Class</label>
            <Select value={classFilter} onValueChange={(v) => { setClassFilter(v); }}>
              <SelectTrigger className="h-10 rounded-xl w-36" data-testid="vc-class-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {meta.classes.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`vc-class-opt-${c}`}>
                    {c === "LKG" || c === "UKG" || c === "Play Group" ? c : `Class ${c}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Section</label>
            <Select value={sectionFilter} onValueChange={setSectionFilter}>
              <SelectTrigger className="h-10 rounded-xl w-32" data-testid="vc-section-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sections</SelectItem>
                {meta.sections.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`vc-section-opt-${s}`}>Sec {s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      {showFilters && (
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Date</label>
          <DatePicker value={date} onChange={setDate} className="h-10 rounded-xl w-44" data-testid="voice-date-input"/>
        </div>
      )}
      {showFilters && showKind && (
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Type</label>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-10 rounded-xl w-36" data-testid="voice-kind-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="vc-kind-opt-all">All types</SelectItem>
              <SelectItem value="absent" data-testid="vc-kind-opt-absent">Absent</SelectItem>
              <SelectItem value="birthday" data-testid="vc-kind-opt-birthday">Birthday</SelectItem>
              <SelectItem value="announcement" data-testid="vc-kind-opt-announcement">Announcement</SelectItem>
              <SelectItem value="wellbeing" data-testid="vc-kind-opt-wellbeing">Wellbeing check-in</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {showFilters && (
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-10 rounded-xl w-40" data-testid="voice-status-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="success">Delivered</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {showActions && isAdmin && (
        <Button
          onClick={onOpenTemplates}
          variant="outline"
          className="h-10 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
          data-testid="open-templates-btn"
        >
          <MessageSquareText className="h-4 w-4 mr-1.5" />
          Templates
        </Button>
      )}
      {showActions && isAdmin && (
        <Button
          onClick={onOpenAnnounce}
          className="h-10 rounded-xl bg-violet-500 hover:bg-violet-600 text-white"
          data-testid="open-announcement-btn"
        >
          <Megaphone className="h-4 w-4 mr-1.5" />
          Announce
        </Button>
      )}
      {showActions && isAdmin && purgePreview.eligible > 0 && (
        <Button
          variant="outline"
          onClick={onOpenPurge}
          className="h-10 rounded-xl border-rose-200 text-rose-600 hover:bg-rose-50"
          data-testid="purge-open-btn"
          title={`${purgePreview.eligible} call(s) older than ${purgePreview.days} days`}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Purge old…
          <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-rose-100 text-rose-700 text-xs" data-testid="purge-eligible-badge">
            {purgePreview.eligible}
          </span>
        </Button>
      )}
    </div>
  );
}

export function PauseToggleCompact({ settings, savingPause, togglePause }) {
  const paused = !!settings.retell_paused;
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border h-10 px-3 ${
        paused ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"
      }`}
      data-testid="retell-pause-panel"
      title={paused ? `Delivery paused: ${settings.retell_pause_reason || "no reason"}` : "Absent-student calls will be placed on submit"}
    >
      {paused ? <PauseCircle className="h-4 w-4 text-amber-600" /> : <PlayCircle className="h-4 w-4 text-emerald-600" />}
      <span className={`text-xs font-medium ${paused ? "text-amber-700" : "text-emerald-700"}`}>
        Delivery agent
      </span>
      <Switch
        checked={!paused}
        onCheckedChange={(on) => togglePause(!on)}
        disabled={savingPause}
        data-testid="retell-pause-toggle"
      />
      <span className="text-[11px] text-slate-500 select-none w-6">{paused ? "Off" : "On"}</span>
    </div>
  );
}

export function SummarySection({ summary }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      <SumTile label="Total" value={summary.total} tone="slate" testid="vc-total" />
      <SumTile label="Delivered" value={summary.success} tone="emerald" testid="vc-success" />
      <SumTile label="Failed" value={summary.failed} tone="rose" testid="vc-failed" />
      <SumTile label="Skipped" value={summary.skipped} tone="amber" testid="vc-skipped" />
      <SumTile label="Pending" value={summary.pending} tone="sky" testid="vc-pending" />
    </div>
  );
}

export function SessionBreakdown({ bySession }) {
  if (!bySession) return null;
  return (
    <div className="grid grid-cols-2 gap-3 mb-4" data-testid="vc-by-session">
      {["morning", "afternoon"].map((sess) => {
        const b = bySession[sess] || { total: 0, success: 0, failed: 0, skipped: 0, pending: 0 };
        return (
          <div key={sess} className="rounded-2xl bg-white border border-slate-200 px-4 py-3 flex items-center gap-4" data-testid={`vc-session-${sess}`}>
            <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${sess === "morning" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>
              {sess === "morning" ? <PlayCircle className="h-4 w-4" /> : <PauseCircle className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{sess === "morning" ? "Morning session" : "Afternoon session"}</div>
              <div className="text-sm text-slate-700">
                <b className="text-slate-900">{b.total}</b> called ·{" "}
                <span className="text-emerald-600">{b.success} delivered</span> ·{" "}
                <span className="text-rose-600">{b.failed} failed</span>
                {b.skipped > 0 && <span className="text-amber-600"> · {b.skipped} skipped</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function RetellPausePanel({ settings, pauseReason, setPauseReason, savingPause, togglePause }) {
  return (
    <div
      className={`mb-6 rounded-2xl border p-5 flex flex-wrap items-center gap-4 ${
        settings.retell_paused ? "bg-amber-50 border-amber-200" : "bg-white border-emerald-200"
      }`}
      data-testid="retell-pause-panel"
    >
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
        settings.retell_paused ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"
      }`}>
        {settings.retell_paused ? <PauseCircle className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>
          Message delivery agent {settings.retell_paused ? <span className="ml-1 text-amber-600">· Paused</span> : <span className="ml-1 text-emerald-600">· Live</span>}
        </div>
        <div className="text-xs text-slate-500">
          {settings.retell_paused
            ? `Reason: ${settings.retell_pause_reason || "(none)"}. New absences will be logged as "skipped" until you resume.`
            : "When attendance is submitted, parents of absent students will be called."}
        </div>
      </div>
      {settings.retell_paused ? null : (
        <Input
          placeholder="Pause reason (optional)"
          value={pauseReason}
          onChange={(e) => setPauseReason(e.target.value)}
          className="h-9 rounded-xl w-56"
          data-testid="pause-reason-input"
        />
      )}
      <div className="flex items-center gap-2">
        <Switch
          checked={!settings.retell_paused}
          onCheckedChange={(on) => togglePause(!on)}
          disabled={savingPause}
          data-testid="retell-pause-toggle"
        />
        <span className="text-xs text-slate-500 select-none">
          {settings.retell_paused ? "Off" : "On"}
        </span>
      </div>
    </div>
  );
}

export function BirthdayCallsPanel({
  birthdayPreview, triggeringBirthday, triggerBirthdayNow,
  schedHour, setSchedHour, schedMinute, setSchedMinute, savingSchedule, saveSchedule,
}) {
  return (
    <div className="mb-6 rounded-2xl border border-pink-200 dark:border-pink-800 bg-white dark:bg-slate-800 p-5" data-testid="birthday-calls-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-pink-50 dark:bg-pink-900/40 text-pink-600 dark:text-pink-300 flex items-center justify-center">
          <Cake className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Birthday Calls Today</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Wishes today&apos;s birthday students via a separate Retell agent.
          </div>
        </div>
        <Button
          onClick={triggerBirthdayNow}
          disabled={triggeringBirthday || !birthdayPreview || birthdayPreview.matched.length === 0 || birthdayPreview.matched.every((s) => s.already_called_today)}
          className="rounded-full bg-pink-500 hover:bg-pink-600 text-white"
          data-testid="trigger-birthday-now-btn"
        >
          {triggeringBirthday ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Cake className="h-4 w-4 mr-1" />}
          {triggeringBirthday ? "Dialling…" : "Wish Now!"}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-xl bg-pink-50/40 dark:bg-pink-950/20 border border-pink-100 dark:border-pink-900" data-testid="birthday-schedule-editor">
        <div className="flex-1 min-w-[180px]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-1">Daily schedule</div>
          <div className="text-sm text-slate-700 dark:text-slate-200">
            Auto-runs every day at{" "}
            <b>
              {birthdayPreview
                ? `${String(birthdayPreview.scheduled_hour).padStart(2, "0")}:${String(birthdayPreview.scheduled_minute ?? 0).padStart(2, "0")} ${birthdayPreview.tz}`
                : "—"}
            </b>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-1">Hour (0–23)</div>
          <Input type="number" min={0} max={23} value={schedHour} onChange={(e) => setSchedHour(e.target.value)}
            className="h-9 w-20 rounded-lg font-mono text-center" data-testid="birthday-sched-hour" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-1">Minute (0–59)</div>
          <Input type="number" min={0} max={59} value={schedMinute} onChange={(e) => setSchedMinute(e.target.value)}
            className="h-9 w-20 rounded-lg font-mono text-center" data-testid="birthday-sched-minute" />
        </div>
        <Button
          onClick={saveSchedule}
          disabled={savingSchedule}
          variant="outline"
          className="h-9 rounded-full border-pink-300 dark:border-pink-700 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900/40"
          data-testid="birthday-sched-save"
        >
          {savingSchedule ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          {savingSchedule ? "Saving…" : "Save schedule"}
        </Button>
      </div>

      {birthdayPreview && birthdayPreview.matched.length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400" data-testid="birthday-empty">No students have a birthday today ({birthdayPreview.run_date}).</div>
      ) : birthdayPreview ? (
        <div className="space-y-1.5" data-testid="birthday-matched-list">
          {birthdayPreview.matched.map((s) => (
            <div key={s.id} className="flex items-center gap-3 text-sm bg-pink-50/50 dark:bg-pink-950/20 border border-pink-100 dark:border-pink-900 rounded-xl px-3 py-2" data-testid={`birthday-row-${s.roll_number}`}>
              <span className="font-mono text-xs text-pink-700 dark:text-pink-300 shrink-0">#{s.roll_number}</span>
              <span className="font-medium text-slate-900 dark:text-slate-100 truncate">{s.name}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Class {s.class_name}-{s.section}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 font-mono ml-auto truncate">{s.parent_phone || "—"}</span>
              {s.already_called_today && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="h-3 w-3" /> Called today
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-400">Loading birthdays…</div>
      )}

      {birthdayPreview && !birthdayPreview.birthday_agent_id && (
        <div className="mt-3 text-xs rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 px-3 py-2">
          No <code className="font-mono">RETELL_BIRTHDAY_AGENT_ID</code> configured — falling back to the default absent-call agent.
        </div>
      )}
    </div>
  );
}

export function AbsentCallsPanel({ absentPreview }) {
  return (
    <div className="mb-6 rounded-2xl border border-rose-200 dark:border-rose-800 bg-white dark:bg-slate-800 p-5" data-testid="absent-calls-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-rose-50 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300 flex items-center justify-center">
          <PhoneOutgoing className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Absent Calls Today</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Auto-placed to parents of absent students when teachers submit attendance.
          </div>
        </div>
        {absentPreview && (
          <div className="flex items-center gap-1.5 text-[11px] font-semibold">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">
              {absentPreview.counts.total} total
            </span>
            {absentPreview.counts.success > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-3 w-3" /> {absentPreview.counts.success}
              </span>
            )}
            {absentPreview.counts.failed > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                {absentPreview.counts.failed} failed
              </span>
            )}
            {absentPreview.counts.pending > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300">
                {absentPreview.counts.pending} pending
              </span>
            )}
          </div>
        )}
      </div>

      {!absentPreview ? (
        <div className="text-xs text-slate-400">Loading absent calls…</div>
      ) : absentPreview.matched.length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400" data-testid="absent-empty">
          No absent calls placed today ({absentPreview.run_date}).
        </div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="absent-matched-list">
          {absentPreview.matched.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 text-sm bg-rose-50/50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900 rounded-xl px-3 py-2"
              data-testid={`absent-row-${r.id}`}
            >
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide text-rose-700 dark:text-rose-300 shrink-0 px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/40">
                {(r.session || "AM").slice(0, 2)}
              </span>
              <span className="font-medium text-slate-900 dark:text-slate-100 truncate">{r.student_name}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Class {r.class_name}-{r.section}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 font-mono ml-auto truncate">{r.parent_phone || "—"}</span>
              <StatusBadgeMini status={r.status} />
            </div>
          ))}
        </div>
      )}

      {absentPreview && !absentPreview.absent_agent_id && (
        <div className="mt-3 text-xs rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 px-3 py-2">
          No <code className="font-mono">RETELL_AGENT_ID</code> configured — absent calls will be logged as skipped.
        </div>
      )}
    </div>
  );
}

export function WebhookPanel({ webhookInfo, syncing, syncWebhook, copyWebhook }) {
  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="retell-webhook-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center">
          <LinkIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900" style={{fontFamily:"Outfit"}}>Retell webhook URL</div>
          <div className="text-xs text-slate-500">Retell will POST call_started, call_ended and call_analyzed events here.</div>
        </div>
        <Button onClick={syncWebhook} disabled={syncing} className="rounded-full bg-sky-500 hover:bg-sky-600" data-testid="sync-webhook-btn">
          <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Push to Retell"}
        </Button>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
        <code className="text-xs text-slate-700 font-mono flex-1 truncate" data-testid="webhook-url">
          {webhookInfo.webhook_url}
        </code>
        <button onClick={copyWebhook} className="text-slate-400 hover:text-slate-700" data-testid="copy-webhook-btn">
          <Copy className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {(webhookInfo.events || []).map((ev) => (
          <span key={ev} className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ev}</span>
        ))}
      </div>
    </div>
  );
}

export function CallLogTable({ loading, items, isAdmin, replayingId, replayCall, deletingId, onDelete }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="voice-calls-table">
      {loading ? (
        <div className="p-10 text-center text-slate-500">Loading call log…</div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-slate-500">
          <div className="inline-flex items-center gap-2 text-slate-400 mb-2"><Filter className="h-4 w-4"/> No calls match your filters.</div>
          <div className="text-xs">When a teacher submits attendance, calls are placed to parents of absent students.</div>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-[0.15em]">
            <tr>
              <th className="px-4 py-3 text-left">When</th>
              <th className="px-4 py-3 text-left">Student</th>
              <th className="px-4 py-3 text-left">Parent</th>
              <th className="px-4 py-3 text-left">Class</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Details</th>
              {isAdmin && <th className="px-4 py-3 text-right w-28">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((c) => {
              const s = statusBadge[c.status] || statusBadge.pending;
              const Icon = s.icon;
              return (
                <tr key={c.id} data-testid={`voice-row-${c.id}`}>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{c.student_name}</span>
                      {c.kind === "birthday" ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-pink-50 text-pink-700 border border-pink-200" title="Birthday call">
                          <Cake className="h-3 w-3" /> Birthday
                        </span>
                      ) : c.kind === "announcement" ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-violet-50 text-violet-700 border border-violet-200" title={c.announcement_text || "Announcement call"}>
                          <Megaphone className="h-3 w-3" /> Announce
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-slate-50 text-slate-600 border border-slate-200" title="Absent-student call">
                          Absent
                        </span>
                      )}
                    </div>
                    {c.kind === "announcement" && c.announcement_text && (
                      <div className="text-xs text-slate-500 mt-1 line-clamp-2 max-w-xs" data-testid={`vc-announce-text-${c.id}`} title={c.announcement_text}>
                        “{c.announcement_text}”
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700">{c.parent_name || "—"}</div>
                    <div className="text-xs text-slate-400 font-mono">{c.parent_phone_e164 || c.parent_phone}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.class_name}-{c.section}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono">{c.date}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${s.tone}`}>
                      <Icon className={`h-3 w-3 ${c.status === "pending" ? "animate-spin" : ""}`} />
                      {s.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-md" title={c.error_message || c.retell_call_id || ""}>
                    {c.status === "success" ? (
                      c.summary || c.transcript ? (
                        <details data-testid={`vc-details-${c.id}`}>
                          <summary className="cursor-pointer text-sky-600 hover:underline">
                            {c.summary ? "Show summary" : "Show transcript"}
                            {c.disconnect_reason ? ` · ${c.disconnect_reason}` : ""}
                          </summary>
                          {c.summary && (
                            <div className="mt-2 p-2 rounded-lg bg-slate-50 text-slate-700 whitespace-pre-wrap">{c.summary}</div>
                          )}
                          {c.transcript && (
                            <pre className="mt-2 p-2 rounded-lg bg-slate-50 text-[11px] text-slate-600 whitespace-pre-wrap max-h-48 overflow-y-auto">{c.transcript}</pre>
                          )}
                        </details>
                      ) : (
                        <span className="font-mono text-slate-400">{c.retell_call_id || "—"}</span>
                      )
                    ) : (
                      <span className="text-rose-600">{c.error_message || "—"}</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => replayCall(c)}
                          disabled={c.status === "pending" || c.status === "success" || replayingId === c.id}
                          data-testid={`replay-btn-${c.id}`}
                          title={c.status === "success" ? "Already delivered — no need to call again" : c.status === "pending" ? "Wait for the in-flight call to finish" : "Retry this call"}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <RotateCcw className={`h-3.5 w-3.5 ${replayingId === c.id ? "animate-spin" : ""}`} />
                          {replayingId === c.id ? "Retrying…" : "Retry"}
                        </button>
                        {(c.status === "failed" || c.status === "skipped") && onDelete && (
                          <button
                            onClick={() => onDelete(c)}
                            disabled={deletingId === c.id}
                            data-testid={`delete-btn-${c.id}`}
                            title="Delete this failed call log"
                            className="inline-flex items-center justify-center h-7 w-7 rounded-full text-rose-500 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <Trash2 className={`h-3.5 w-3.5 ${deletingId === c.id ? "animate-pulse" : ""}`} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function PurgeDialog({
  open, onOpenChange, purgeDays, setPurgeDays, purgePreview,
  purgeTypedConfirm, setPurgeTypedConfirm, purging, runPurge,
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Purge old voice-call logs?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes voice-call rows older than the chosen number of days. Leave it at <b>90</b> for the default retention; lower it for a one-off demo cleanup.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3 px-1">
          <div className="flex items-center gap-3">
            <label htmlFor="purge-days" className="text-sm text-slate-600 w-44">Older than (days)</label>
            <Input
              id="purge-days"
              type="number"
              min="1"
              max="3650"
              value={purgeDays}
              onChange={(e) => setPurgeDays(e.target.value)}
              className="h-10 rounded-xl"
              data-testid="purge-days-input"
            />
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700" data-testid="purge-eligible-preview">
            <b className="text-rose-600">{purgePreview.eligible}</b> call{purgePreview.eligible === 1 ? "" : "s"} {purgePreview.eligible === 1 ? "matches" : "match"} this threshold and will be deleted.
          </div>
          {parseInt(purgeDays, 10) < 30 && (
            <div className="space-y-2" data-testid="purge-strict-confirm">
              <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                Aggressive cleanup. Type <span className="font-mono font-semibold">PURGE</span> below to confirm.
              </div>
              <Input
                value={purgeTypedConfirm}
                onChange={(e) => setPurgeTypedConfirm(e.target.value)}
                placeholder="Type PURGE"
                className="h-10 rounded-xl"
                data-testid="purge-strict-input"
              />
            </div>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="purge-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); runPurge(); }}
            disabled={
              purging ||
              purgePreview.eligible === 0 ||
              (parseInt(purgeDays, 10) < 30 && purgeTypedConfirm.trim().toUpperCase() !== "PURGE")
            }
            className="bg-rose-500 hover:bg-rose-600 disabled:opacity-50"
            data-testid="purge-confirm-btn"
          >
            {purging ? "Purging…" : `Yes, delete ${purgePreview.eligible}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


export function ReachabilityPanel() {
  const [days, setDays] = useState("30");
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days: parseInt(days, 10) };
      if (kind !== "all") params.kind = kind;
      const res = await api.get("/admin/call-analytics", { params });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, kind]);

  const s = data?.summary;
  const nr = data?.never_reached || [];
  const copyPhone = (p) => { try { navigator.clipboard?.writeText(p); toast.success("Phone number copied"); } catch { /* noop */ } };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800 p-5 mb-6" data-testid="call-reachability-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Parent reachability</div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Who's not answering calls</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Parents we tried to call but never got through — follow up another way.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-[150px] rounded-full" data-testid="reach-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All call types</SelectItem>
              <SelectItem value="absent">Absence</SelectItem>
              <SelectItem value="birthday">Birthday</SelectItem>
              <SelectItem value="announcement">Announcement</SelectItem>
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[130px] rounded-full" data-testid="reach-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-400" data-testid="reach-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading analytics…
        </div>
      ) : !s || s.total_students_called === 0 ? (
        <div className="text-center py-10 text-slate-400" data-testid="reach-empty">No calls placed in this period.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <SumTile label="Reach rate" value={s.reach_rate === null ? "—" : `${s.reach_rate}%`} tone={s.reach_rate !== null && s.reach_rate < 60 ? "rose" : "emerald"} testid="reach-rate" />
            <SumTile label="Never reached" value={s.never_reached} tone="rose" testid="reach-never" />
            <SumTile label="Reached" value={s.reached} tone="emerald" testid="reach-reached" />
            <SumTile label="Parents called" value={s.total_students_called} tone="slate" testid="reach-called" />
            <SumTile label="Total calls" value={s.total_calls} tone="slate" testid="reach-total-calls" />
            <SumTile label="Answered" value={s.total_answered} tone="emerald" testid="reach-answered" />
            <SumTile label="No answer" value={s.total_no_answer} tone="amber" testid="reach-noanswer" />
            <SumTile label="Failed" value={s.total_failed} tone="rose" testid="reach-failed" />
          </div>

          {nr.length === 0 ? (
            <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm p-4 flex items-center gap-2" data-testid="reach-allgood">
              <CheckCircle2 className="h-4 w-4" /> Every parent we called was reached at least once. 🎉
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800" data-testid="reach-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">Student</th>
                    <th className="text-left px-4 py-3">Class</th>
                    <th className="text-left px-4 py-3">Parent</th>
                    <th className="text-left px-4 py-3">Phone</th>
                    <th className="text-center px-3 py-3">Attempts</th>
                    <th className="text-center px-3 py-3">No answer</th>
                    <th className="text-center px-3 py-3">Failed</th>
                    <th className="text-left px-4 py-3">Last tried</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {nr.map((r) => (
                    <tr key={r.student_id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`reach-row-${r.student_id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{r.student_name}{r.roll_number ? <span className="text-slate-400 font-normal"> · #{r.roll_number}</span> : null}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{[r.class_name, r.section].filter(Boolean).join("-")}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{r.parent_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300 whitespace-nowrap">{r.parent_phone || "—"}</td>
                      <td className="px-3 py-3 text-center font-semibold text-slate-700 dark:text-slate-200">{r.attempts}</td>
                      <td className="px-3 py-3 text-center text-amber-600 dark:text-amber-400">{r.no_answer}</td>
                      <td className="px-3 py-3 text-center text-rose-600 dark:text-rose-400">{r.failed}</td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">{r.last_attempt || "—"}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          {r.parent_phone && (
                            <>
                              <a href={`tel:${r.parent_phone.replace(/\s/g, "")}`} title="Call parent" className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10" data-testid={`reach-call-${r.student_id}`}>
                                <PhoneOutgoing className="h-4 w-4" />
                              </a>
                              <button onClick={() => copyPhone(r.parent_phone)} title="Copy number" className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid={`reach-copy-${r.student_id}`}>
                                <Copy className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
